import CodeMirror from 'codemirror';
import 'codemirror/lib/codemirror.css';
import 'codemirror/mode/ruby/ruby';
import 'codemirror/addon/edit/closebrackets';
import 'codemirror/addon/edit/matchbrackets';
import 'codemirror/addon/edit/trailingspace';
import 'codemirror/addon/search/match-highlighter';
import 'codemirror/addon/selection/active-line';
import 'codemirror/theme/dracula.css';

// Busca dentro do editor (Ctrl/Cmd-F, Ctrl/Cmd-G) e "ir para linha" (Alt-G).
import 'codemirror/addon/dialog/dialog';
import 'codemirror/addon/dialog/dialog.css';
import 'codemirror/addon/search/searchcursor';
import 'codemirror/addon/search/search';
import 'codemirror/addon/search/jump-to-line';

// Dobrar blocos, para navegar em código longo.
import 'codemirror/addon/fold/foldcode';
import 'codemirror/addon/fold/foldgutter';
import 'codemirror/addon/fold/foldgutter.css';
import 'codemirror/addon/fold/brace-fold';
import 'codemirror/addon/fold/indent-fold';
import 'codemirror/addon/fold/comment-fold';

// Comentar/descomentar seleção.
import 'codemirror/addon/comment/comment';

// Tela cheia (F11 / Esc, e o botão da barra).
import 'codemirror/addon/display/fullscreen';
import 'codemirror/addon/display/fullscreen.css';

import '../styles/code_editor.css';

const TEXTAREA_ID = 'agent_custom_code';
const HEIGHT_STORAGE_KEY = 'huginn.customCodeEditorHeight';
const DEFAULT_HEIGHT = 420;
const MIN_HEIGHT = 160;

// localStorage pode lançar (modo privado, cookies bloqueados) ou simplesmente não existir. Nesse
// caso a altura só não persiste entre sessões — o editor continua funcionando.
const storedHeight = () => {
  try {
    const parsed = parseInt(window.localStorage.getItem(HEIGHT_STORAGE_KEY), 10);
    if (isFinite(parsed) && parsed >= MIN_HEIGHT) return parsed;
  } catch (e) {
    // segue com o default
  }
  return DEFAULT_HEIGHT;
};

const rememberHeight = (height) => {
  try {
    window.localStorage.setItem(HEIGHT_STORAGE_KEY, String(Math.round(height)));
  } catch (e) {
    // sem persistência; nada a fazer
  }
};

// Alça de arraste na borda inferior. O CodeMirror gerencia a própria altura, então `resize: vertical`
// no CSS não funciona de forma confiável — é preciso chamar `setSize` explicitamente.
const addResizeHandle = (editor) => {
  const wrapper = editor.getWrapperElement();
  const handle = document.createElement('div');
  handle.className = 'cm-resize-handle';
  handle.setAttribute('title', 'Arraste para redimensionar');
  wrapper.parentNode.insertBefore(handle, wrapper.nextSibling);

  let startY = null;
  let startHeight = null;

  const onMove = (event) => {
    if (startY === null) return;
    const next = Math.max(MIN_HEIGHT, startHeight + (event.clientY - startY));
    editor.setSize(null, next);
  };

  const onUp = () => {
    if (startY === null) return;
    startY = null;
    document.body.classList.remove('cm-resizing');
    rememberHeight(wrapper.getBoundingClientRect().height);
  };

  handle.addEventListener('mousedown', (event) => {
    event.preventDefault();
    startY = event.clientY;
    startHeight = wrapper.getBoundingClientRect().height;
    document.body.classList.add('cm-resizing');
  });

  document.addEventListener('mousemove', onMove);
  document.addEventListener('mouseup', onUp);

  // Duplo clique na alça volta para a altura padrão.
  handle.addEventListener('dblclick', () => {
    editor.setSize(null, DEFAULT_HEIGHT);
    rememberHeight(DEFAULT_HEIGHT);
  });
};

const addToolbar = (editor) => {
  const wrapper = editor.getWrapperElement();
  const bar = document.createElement('div');
  bar.className = 'cm-toolbar';

  const button = (label, title, onClick) => {
    const el = document.createElement('button');
    el.type = 'button'; // sem isto o botão submete o formulário do agente
    el.className = 'btn btn-default btn-xs';
    el.textContent = label;
    el.setAttribute('title', title);
    el.addEventListener('click', (event) => {
      event.preventDefault();
      onClick();
      editor.focus();
    });
    return el;
  };

  bar.appendChild(
    button('Tela cheia', 'Alternar tela cheia (F11, Esc para sair)', () => {
      editor.setOption('fullScreen', !editor.getOption('fullScreen'));
    })
  );
  bar.appendChild(
    button('Buscar', 'Buscar no código (Ctrl/Cmd-F)', () => {
      editor.execCommand('find');
    })
  );
  bar.appendChild(
    button('Comentar', 'Comentar/descomentar a seleção (Ctrl/Cmd-/)', () => {
      editor.execCommand('toggleComment');
    })
  );

  const hint = document.createElement('span');
  hint.className = 'cm-toolbar-hint';
  hint.textContent = 'F11 tela cheia · Ctrl/Cmd-F buscar · Ctrl/Cmd-/ comentar';
  bar.appendChild(hint);

  wrapper.parentNode.insertBefore(bar, wrapper);
};

const buildEditor = (textarea) => {
  const isMac = /Mac/.test(navigator.platform);
  const cmdKey = isMac ? 'Cmd' : 'Ctrl';

  const editor = CodeMirror.fromTextArea(textarea, {
    lineNumbers: true,
    mode: 'ruby',
    theme: 'dracula',
    autoCloseBrackets: true,
    matchBrackets: true,
    highlightSelectionMatches: { showToken: /\w/, annotateScrollbar: true },
    styleActiveLine: true,
    // Linhas longas antes quebravam em scroll horizontal, o que tornava difícil ler o código.
    lineWrapping: true,
    indentUnit: 2,
    tabSize: 2,
    indentWithTabs: false,
    showTrailingSpace: true,
    foldGutter: true,
    gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
    extraKeys: {
      F11: (cm) => cm.setOption('fullScreen', !cm.getOption('fullScreen')),
      Esc: (cm) => {
        if (cm.getOption('fullScreen')) cm.setOption('fullScreen', false);
      },
      [`${cmdKey}-/`]: 'toggleComment',
      // Tab insere indentação em vez de tirar o foco do editor.
      Tab: (cm) => {
        if (cm.somethingSelected()) return cm.indentSelection('add');
        return cm.execCommand('insertSoftTab');
      },
      'Shift-Tab': (cm) => cm.indentSelection('subtract'),
    },
  });

  editor.setSize(null, storedHeight());
  addToolbar(editor);
  addResizeHandle(editor);

  return editor;
};

// Guardamos a instância no próprio elemento: sem isto, alternar o tipo de agente para
// `Agents::CustomAgent` mais de uma vez chamava `fromTextArea` de novo no mesmo textarea e empilhava
// editores, um por troca de tipo.
const initializeCodeMirror = () => {
  const textarea = document.getElementById(TEXTAREA_ID);

  // O formulário do agente é de duas colunas (campos à esquerda, descrição à direita), o que deixava
  // o editor com cerca de metade da largura da página — apertado para escrever código. Esta classe
  // faz as colunas empilharem só quando o editor de código está em tela, dando a largura inteira a
  // ele sem alterar o layout dos outros tipos de agente.
  document.body.classList.toggle('custom-code-layout', !!textarea);

  if (!textarea || textarea.dataset.codemirrorAttached === 'true') return;

  textarea.dataset.codemirrorAttached = 'true';
  buildEditor(textarea);
};

$(document).ready(() => {
  initializeCodeMirror();

  // Trocar o tipo de agente substitui o HTML inteiro de `.agent-options` por AJAX
  // (agent-edit-page.js, `$(".agent-options").html(json.form_options)`). A versão anterior tentava
  // montar o editor num `setTimeout` de 500ms, o que era uma corrida contra essa resposta: se ela
  // demorasse mais, o editor era montado no textarea antigo e destruído junto com ele um instante
  // depois — e o campo ficava sem editor nenhum. Observar o container elimina o palpite de tempo.
  const optionsContainer = document.querySelector('.agent-options');

  if (optionsContainer && window.MutationObserver) {
    new MutationObserver(initializeCodeMirror).observe(optionsContainer, {
      childList: true,
      subtree: true,
    });
  } else {
    $('#agent_type').on('change', (e) => {
      if (e.currentTarget.value === 'Agents::CustomAgent') {
        setTimeout(initializeCodeMirror, 500);
      }
    });
  }
});
